import { Button, FormDialog, FormError, Label, Textarea } from "@simplicity/ui";
import { useId, useState } from "react";
import { noteError, releaseDialogCopy, type ReleaseIntent } from "./releasesModel";
import type { AgentReleaseRecord, AgentReleases } from "./types";
import { usePromoteAgentRelease, useRollbackAgentRelease } from "./useOnsite";

function useSubmitRelease(onDone: (record: AgentReleaseRecord) => void) {
  const promote = usePromoteAgentRelease();
  const rollback = useRollbackAgentRelease();
  const submit = (intent: ReleaseIntent, note: string) => {
    const options = { onSuccess: onDone };
    if (intent.kind === "promote") {
      promote.mutate({ digest: intent.candidate.digest, note: note.trim() }, options);
    } else {
      rollback.mutate(note.trim(), options);
    }
  };
  return {
    submit,
    pending: promote.isPending || rollback.isPending,
    error: promote.error ?? rollback.error,
  };
}

function NoteField({ note, onChange }: { note: string; onChange: (note: string) => void }) {
  const id = useId();
  const error = note === "" ? null : noteError(note);
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="form-label">
        Note (required)
      </Label>
      <Textarea
        id={id}
        value={note}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Why this release: what it changes or fixes"
        aria-invalid={error ? true : undefined}
      />
      {error && <FormError>{error}</FormError>}
    </div>
  );
}

/**
 * Confirms a promote or a rollback: what changes, which workspaces will read Behind, and a note the
 * release history keeps. Mounted only while open, so each opening starts blank.
 */
export function ReleaseDialog({
  intent,
  releases,
  onClose,
  onDone,
}: {
  intent: ReleaseIntent;
  releases: AgentReleases;
  onClose: () => void;
  onDone: (record: AgentReleaseRecord) => void;
}) {
  const [note, setNote] = useState("");
  const release = useSubmitRelease(onDone);
  const copy = releaseDialogCopy(intent, releases);
  const footer = (
    <>
      <Button variant="outline" onClick={onClose} disabled={release.pending}>
        Cancel
      </Button>
      <Button
        onClick={() => release.submit(intent, note)}
        disabled={noteError(note) !== null || release.pending}
      >
        {release.pending ? "Saving..." : copy.confirmLabel}
      </Button>
    </>
  );
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && !release.pending && onClose()}
      title={copy.title}
      description={copy.description}
      footer={footer}
    >
      <div className="space-y-3 text-sm">
        <ul className="space-y-1 text-muted-foreground">
          {copy.facts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
        <p className="text-foreground">{copy.behind}</p>
        <NoteField note={note} onChange={setNote} />
        {release.error && (
          <FormError assertive className="whitespace-pre-line">
            {release.error.message}
          </FormError>
        )}
      </div>
    </FormDialog>
  );
}
